/**
 * Agent Configuration Page
 * Edit agent configuration with tabbed interface
 *
 * Tab Structure:
 * - Hooks: Feature toggles + Hooks pipeline
 * - Basic: Core settings (name, model, LLM params, team config when enabled)
 * - Memory: Memory system config (conditional)
 * - Knowledge: Knowledge base config (conditional)
 * - Classifier: Response classifier + Skills (conditional)
 * - Tools: Tool configuration
 * - Prompts: System prompts + Templates
 * - Advanced: Advanced settings
 * - Schemas: Output schemas (conditional)
 * - Versions: Version history
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAgentConfigQuery,
  useUpdateAgentMutation,
  useAgentVersionsQuery,
  useDeactivateAgentMutation,
  useReactivateAgentMutation,
  isConflictError,
  AgentConfigConflictError,
  QUERY_KEYS,
} from '@/lib/queries';
import { Loading, ErrorMessage } from '@/components/common';
import { Modal } from '@/components/common/Modal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { FieldSection } from '@/components/fields/FieldSection';
import { HooksConfigPanel } from '@/features/hooks';
import { ToolsBrowser } from '@/features/tools/components/ToolsBrowser';
import { MCPToolsBrowser } from '@/features/tools/components/MCPToolsBrowser';
import { AutoToolMatchModal } from '@/features/tools/components/AutoToolMatchModal';
import { useAutoToolMatch } from '@/features/tools/hooks';
import {
  VersionHistory,
  CreateAgentDialog,
  TemplateSelector,
  AgentPromptPreview,
  BasicTab,
  MemoryTab,
  KnowledgeTab,
  AstrologyTab,
  PersonalityTab,
  EmotionTab,
  ClassifierTab,
  AdvancedTab,
  ToolComboSection,
} from '@/features/agent-config/components';
import { MERGED_FIELD_CONFIG as FIELD_CONFIG, getConfigValue } from '@/config/fieldConfig';
import { useFieldValidation } from '@/lib/validation/useFieldValidation';
import { FIELD_SECTIONS } from '@/config/fieldSections';
import { getVisibleTabs, type TabId } from '@/config/tabConfig';
import { useI18n } from '@/i18n';
import { createAgentConfigNavigation } from '@/lib/navigation';
import type { AgentConfiguration, ToolMode } from '@/types';
import {
  ArrowLeft,
  Copy,
  History,
  Settings,
  Wrench,
  Sparkles,
  FileText,
  Brain,
  Filter,
  Cog,
  BookOpen,
  Star,
  Heart,
  Eye,
  X,
  Wand2,
  Fingerprint,
} from 'lucide-react';

type BrowserMode = ToolMode | 'mcp';

// Icon mapping for tabs
const TAB_ICONS: Record<TabId, React.ComponentType<{ className?: string }>> = {
  basic: Settings,
  hooks: Sparkles,
  memory: Brain,
  knowledge: BookOpen,
  astrology: Star,
  personality: Fingerprint,
  emotion: Heart,
  classifier: Filter,
  tools: Wrench,
  prompts: FileText,
  advanced: Cog,
  versions: History,
};

export const AgentConfigPage: React.FC = () => {
  const { agentType } = useParams<{ agentType: string }>();
  const navigate = useNavigate();
  const { messages, language } = useI18n();
  const t = messages.agentConfig;
  const tc = messages.common;
  const ta = messages.actions;
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [formData, setFormData] = useState<Partial<AgentConfiguration>>({});
  const [isDirty, setIsDirty] = useState(false);
  // Image size/aspect/quality legality vs the selected model's capability
  // contract (Task 3.3). An illegal stored value blocks submit (spec I4).
  const [imageConfigValid, setImageConfigValid] = useState(true);
  // Section to highlight after tab navigation
  const [highlightSection, setHighlightSection] = useState<string | null>(null);

  // Navigation context for cross-tab/page navigation
  const navigation = useMemo(
    () => createAgentConfigNavigation(setActiveTab),
    []
  );

  // Tool browser state
  const [toolBrowserOpen, setToolBrowserOpen] = useState(false);
  const [toolBrowserMode, setToolBrowserMode] = useState<BrowserMode>('custom');
  const [toolBrowserField, setToolBrowserField] = useState<string>('');

  // Auto tool match hook
  const autoToolMatch = useAutoToolMatch({
    currentCustomTools: formData.custom_tools,
    currentBuiltinTools: formData.builtin_tools,
    currentMcpServers: formData.mcp_servers,
  });

  // Optimistic lock: track version loaded from server and conflict state
  const [loadedVersion, setLoadedVersion] = useState<number | undefined>(undefined);
  const [conflictInfo, setConflictInfo] = useState<AgentConfigConflictError | null>(null);
  const forceOverrideRef = useRef(false);
  const queryClient = useQueryClient();

  // Copy dialog state
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  // Prompt preview dialog state
  const [showPromptPreview, setShowPromptPreview] = useState(false);


  // Sticky toolbar state
  const [showStickyToolbar, setShowStickyToolbar] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Queries
  const {
    data: agentConfig,
    isLoading,
    error,
    refetch,
  } = useAgentConfigQuery(agentType || '', !!agentType);

  // Query for versions to get the active version number
  const { data: versions } = useAgentVersionsQuery(agentType || '', !!agentType);

  // Get the active version from versions list
  const activeVersion = useMemo(() => {
    return versions?.items?.find((v: { is_active: boolean }) => v.is_active);
  }, [versions]);

  // Mutations
  const updateMutation = useUpdateAgentMutation();
  const deactivateMutation = useDeactivateAgentMutation();
  const reactivateMutation = useReactivateAgentMutation();

  const handleToggleActive = useCallback(async () => {
    if (!agentConfig || !agentType) return;

    const isActive = agentConfig.is_active;
    const confirmMsg = isActive ? t.confirmDeactivate : t.confirmActivate;

    if (!window.confirm(confirmMsg)) return;

    try {
      if (isActive) {
        await deactivateMutation.mutateAsync(agentType);
      } else {
        await reactivateMutation.mutateAsync(agentType);
      }
    } catch (err) {
      console.error('Failed to toggle agent status:', err);
    }
  }, [agentConfig, agentType, t, deactivateMutation, reactivateMutation]);

  // Validation - use formData as context (appContext defined later includes model lists)
  const {
    errors: validationErrors,
    validateSingleField,
    clearAllErrors,
    hasErrors: hasValidationErrors,
  } = useFieldValidation(formData);

  // Initialize form data when config loads
  useEffect(() => {
    if (agentConfig?.configuration) {
      setFormData(agentConfig.configuration);
      setIsDirty(false);
      setLoadedVersion(agentConfig.version);
      clearAllErrors();
    }
  }, [agentConfig, clearAllErrors]);

  // Sticky toolbar: show when header scrolls out of view
  // Note: agentConfig dependency ensures this runs after loading completes and header element exists
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    // Find the scrolling container (main element from MainLayout)
    const scrollContainer = header.closest('main');
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Get header's position relative to the scroll container
      const headerRect = header.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Show sticky toolbar when header top is above the container top
      const headerIsAbove = headerRect.bottom < containerRect.top + 10;
      setShowStickyToolbar(headerIsAbove);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [agentConfig]);

  // Handle field change
  const handleFieldChange = useCallback((name: string, value: any) => {
    setFormData((prev) => {
      // Handle nested fields (e.g., memory.enabled)
      if (name.includes('.')) {
        const parts = name.split('.');
        const newData = { ...prev };
        let current: any = newData;

        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          } else {
            current[parts[i]] = { ...current[parts[i]] };
          }
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
        return newData;
      }

      return { ...prev, [name]: value };
    });
    setIsDirty(true);

    // Validate the field
    const fieldConfig = FIELD_CONFIG[name];
    if (fieldConfig) {
      validateSingleField(name, value, fieldConfig);
    }
  }, [validateSingleField]);

  // Handle save
  const handleSave = async () => {
    if (!agentType) return;

    try {
      const configToSave = { ...formData };

      // Clean up skills: filter empty lines from patterns and example_queries
      if (configToSave.hooks?.classifier?.skills) {
        configToSave.hooks = {
          ...configToSave.hooks,
          classifier: {
            ...configToSave.hooks.classifier,
            skills: configToSave.hooks.classifier.skills.map(skill => ({
              ...skill,
              patterns: skill.patterns?.filter((p: string) => p.trim()) || [],
              example_queries: skill.example_queries?.filter((q: string) => q.trim()) || [],
            })),
          },
        };
      }

      // Clean up legacy: separate builtin tools from custom_tools array
      if (Array.isArray(configToSave.custom_tools)) {
        const builtinInCustom = configToSave.custom_tools.filter(
          (t: string) => typeof t === 'string' && t.startsWith('builtin:')
        );
        const pureCustomTools = configToSave.custom_tools.filter(
          (t: string) => typeof t !== 'string' || !t.startsWith('builtin:')
        );

        // Move builtin tools to builtin_tools field
        if (builtinInCustom.length > 0) {
          // Get existing builtin_tools config
          let existingToolkits: string[] = [];
          let existingEnabledTools: string[] = [];

          if (Array.isArray(configToSave.builtin_tools)) {
            existingToolkits = configToSave.builtin_tools;
          } else if (configToSave.builtin_tools && typeof configToSave.builtin_tools === 'object') {
            existingToolkits = configToSave.builtin_tools.toolkits || [];
            existingEnabledTools = configToSave.builtin_tools.enabled_tools || [];
          }

          // Merge and dedupe toolkits
          const mergedToolkits = [...new Set([...existingToolkits, ...builtinInCustom])];

          // Build new config preserving enabled_tools
          const newBuiltinConfig: { toolkits: string[]; enabled_tools?: string[] } = {
            toolkits: mergedToolkits,
          };
          if (existingEnabledTools.length > 0) {
            newBuiltinConfig.enabled_tools = existingEnabledTools;
          }

          configToSave.builtin_tools = newBuiltinConfig;

          // Update custom_tools
          configToSave.custom_tools = pureCustomTools.length > 0 ? pureCustomTools : null;

          console.log('[Save] Migrated builtin tools from custom_tools:', builtinInCustom);
        }
      }

      const effectiveExpectedVersion = forceOverrideRef.current ? undefined : loadedVersion;
      forceOverrideRef.current = false;
      try {
        await updateMutation.mutateAsync({
          agentType,
          config: configToSave,
          description: t.updatedViaAdmin,
          expectedVersion: effectiveExpectedVersion,
        });
        setIsDirty(false);
        clearAllErrors();
      } catch (err) {
        if (isConflictError(err)) {
          setConflictInfo(err);
          return;
        }
        throw err;
      }
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };

  // Handle tab navigation (from FeatureToggles)
  const handleNavigateToTab = useCallback((tabId: string, sectionId?: string) => {
    setActiveTab(tabId as TabId);
    if (sectionId) {
      setHighlightSection(sectionId);
      // Clear highlight after animation completes
      setTimeout(() => setHighlightSection(null), 2000);
    }
  }, []);

  // Handle auto tool match
  const handleAutoToolMatch = () => {
    // Collect all prompt-related text for matching
    const instructions = formData.instructions || '';
    const additionalContext = formData.additional_context || '';
    const promptText = [instructions, additionalContext].filter(Boolean).join('\n');

    if (!promptText.trim()) {
      return;
    }

    autoToolMatch.openMatch(promptText);
  };

  // Handle auto tool match confirm
  const handleAutoToolMatchConfirm = () => {
    const configs = autoToolMatch.getUpdatedConfigs();

    // Update custom tools
    if (configs.customTools) {
      try {
        const parsed = JSON.parse(configs.customTools);
        handleFieldChange('custom_tools', parsed);
      } catch { /* ignore */ }
    }

    // Update builtin tools
    if (configs.builtinTools) {
      try {
        const parsed = JSON.parse(configs.builtinTools);
        handleFieldChange('builtin_tools', parsed);
      } catch { /* ignore */ }
    }

    // Update MCP servers
    if (configs.mcpServers) {
      try {
        const parsed = JSON.parse(configs.mcpServers);
        handleFieldChange('mcp_servers', parsed);
      } catch { /* ignore */ }
    }
  };

  // Handle tool browser
  const handleBrowseTools = (fieldName: string) => {
    let mode: BrowserMode = 'custom';
    if (fieldName === 'builtin_tools') {
      mode = 'builtin';
    } else if (fieldName === 'mcp_servers') {
      mode = 'mcp';
    }
    setToolBrowserMode(mode);
    setToolBrowserField(fieldName);
    setToolBrowserOpen(true);
  };

  const handleToolsApply = (config: string) => {
    try {
      // Empty config means clear the field (set to null)
      if (!config || config.trim() === '' || config.trim() === '[]') {
        handleFieldChange(toolBrowserField, null);
        setToolBrowserOpen(false);
        return;
      }

      const parsed = JSON.parse(config);

      // Also treat empty arrays as null
      if (Array.isArray(parsed) && parsed.length === 0) {
        handleFieldChange(toolBrowserField, null);
        setToolBrowserOpen(false);
        return;
      }

      // For custom_tools array, separate builtin tools immediately
      if (toolBrowserField === 'custom_tools' && Array.isArray(parsed)) {
        const builtinTools = parsed.filter(
          (t: string) => typeof t === 'string' && t.startsWith('builtin:')
        );
        const customTools = parsed.filter(
          (t: string) => typeof t !== 'string' || !t.startsWith('builtin:')
        );

        // If there are builtin tools, migrate them
        if (builtinTools.length > 0) {
          // Get existing builtin_tools config
          const existingBuiltin = formData.builtin_tools;
          let existingToolkits: string[] = [];
          let existingEnabledTools: string[] = [];

          if (Array.isArray(existingBuiltin)) {
            // Legacy array format
            existingToolkits = existingBuiltin;
          } else if (existingBuiltin && typeof existingBuiltin === 'object') {
            // New object format
            existingToolkits = existingBuiltin.toolkits || [];
            existingEnabledTools = existingBuiltin.enabled_tools || [];
          }

          // Merge and dedupe toolkits
          const mergedToolkits = [...new Set([...existingToolkits, ...builtinTools])];

          // Build new config preserving enabled_tools
          const newBuiltinConfig: { toolkits: string[]; enabled_tools?: string[] } = {
            toolkits: mergedToolkits,
          };
          if (existingEnabledTools.length > 0) {
            newBuiltinConfig.enabled_tools = existingEnabledTools;
          }

          // Update builtin_tools field
          handleFieldChange('builtin_tools', newBuiltinConfig);

          // Update custom_tools field (only non-builtin)
          handleFieldChange('custom_tools', customTools.length > 0 ? customTools : null);

          console.log('[ToolsApply] Migrated builtin tools:', builtinTools);
          setToolBrowserOpen(false);
          return;
        }

        // No builtin tools, just set custom_tools
        handleFieldChange(toolBrowserField, customTools.length > 0 ? customTools : null);
        setToolBrowserOpen(false);
        return;
      }

      // For object config, check if it's effectively empty
      if (typeof parsed === 'object' && parsed !== null) {
        const hasToolkits = parsed.toolkits?.length > 0;
        const hasServers = parsed.servers?.length > 0;
        const hasModules = parsed.modules?.length > 0;
        const hasEnabledTools = parsed.enabled_tools?.length > 0;
        if (!hasToolkits && !hasServers && !hasModules && !hasEnabledTools) {
          handleFieldChange(toolBrowserField, null);
        } else {
          handleFieldChange(toolBrowserField, parsed);
        }
      } else {
        handleFieldChange(toolBrowserField, parsed);
      }
    } catch (e) {
      console.error('Failed to parse tools config:', e);
      handleFieldChange(toolBrowserField, null);
    }
    setToolBrowserOpen(false);
  };

  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, Array<{ name: string; schema: typeof FIELD_CONFIG[string] }>> = {};

    Object.entries(FIELD_CONFIG).forEach(([name, schema]) => {
      const section = schema.section;
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push({ name, schema });
    });

    return groups;
  }, []);

  // App context for field rendering (model selectors self-fetch from catalog)
  const appContext = useMemo(() => ({
    ...formData,
  }), [formData]);

  // Handle config update from HooksConfigPanel (updates entire config object)
  const handleHooksConfigUpdate = useCallback((newConfig: Partial<AgentConfiguration>) => {
    setFormData(newConfig);
    setIsDirty(true);
  }, []);

  // Get visible tabs - all tabs are always visible
  const visibleTabs = useMemo(() => {
    return getVisibleTabs();
  }, []);

  // Get current field value
  const getFieldValue = (name: string) => {
    return getConfigValue(formData, name);
  };

  // Tab label mapping - basic tab changes based on team mode
  const isTeamMode = formData.agent_mode === 'team';
  const getTabLabel = (tabId: TabId): string => {
    const labels: Record<TabId, string> = {
      basic: isTeamMode ? t.tabTeam : t.tabBasic,
      hooks: t.tabHooks,
      memory: t.tabMemory,
      knowledge: t.tabKnowledge,
      astrology: t.tabAstrology,
      personality: t.tabPersonality,
      emotion: t.tabEmotion,
      classifier: t.tabClassifier,
      tools: t.tabTools,
      prompts: t.tabPrompt,
      advanced: t.tabAdvanced,
      versions: t.tabVersions,
    };
    return labels[tabId];
  };

  if (isLoading) {
    return <Loading message={t.loadingConfig} />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage error={error.message} onRetry={() => refetch()} />
        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate('/')}>
            {t.backToList}
          </Button>
        </div>
      </div>
    );
  }

  if (!agentConfig) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <p className="text-slate-500">{t.agentNotFound}</p>
          <Button className="mt-4" onClick={() => navigate('/')}>
            {t.backToList}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Sticky Toolbar - appears when header scrolls out of view */}
      <div
        className={`sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm -mx-6 px-6 py-2 transition-all duration-200 ${
          showStickyToolbar ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none h-0 py-0 border-0 overflow-hidden'
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Left: Back button + Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-slate-700 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-slate-400">/</span>
            <span className="text-slate-500 truncate max-w-[150px]">
              {agentConfig.configuration.name}
            </span>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-700">
              {getTabLabel(activeTab)}
            </span>
            <span className="text-slate-300 mx-1">|</span>
            <span className="text-slate-400 font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
              {agentType}
            </span>
            <span className="text-xs text-slate-400">
              v{activeVersion?.version || agentConfig.version}
            </span>
            <button
              onClick={handleToggleActive}
              disabled={deactivateMutation.isPending || reactivateMutation.isPending}
              className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                agentConfig.is_active ? 'bg-emerald-500' : 'bg-slate-300'
              } ${(deactivateMutation.isPending || reactivateMutation.isPending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={agentConfig.is_active ? t.deactivateAgent : t.activateAgent}
              role="switch"
              aria-checked={agentConfig.is_active}
            >
              <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform duration-200 ${
                agentConfig.is_active ? 'translate-x-3.5' : 'translate-x-0.5'
              }`} />
            </button>
            <span className={`text-xs ${agentConfig.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
              {agentConfig.is_active ? t.active : t.inactive}
            </span>
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCopyDialog(true)}
              title={t.copy}
            >
              <Copy className="w-4 h-4 mr-1" />
              {t.copy}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPromptPreview(true)}
              title={t.promptPreview?.title}
            >
              <Eye className="w-4 h-4 mr-1" />
              {t.promptPreview?.buttonLabel}
            </Button>
            <span className="text-slate-200">|</span>
            {isDirty && (
              <span className="text-xs text-amber-600 px-2">{t.unsavedChanges}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (agentConfig?.configuration) {
                  setFormData(agentConfig.configuration);
                  setIsDirty(false);
                  clearAllErrors();
                }
              }}
              disabled={!isDirty}
            >
              {tc.reset}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending || hasValidationErrors || !imageConfigValid}
            >
              {updateMutation.isPending ? t.saving : ta.saveChanges}
            </Button>
          </div>
        </div>
      </div>

      {/* Header */}
      <div ref={headerRef} className="space-y-3">
        {/* Title Row */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="text-slate-500 hover:text-slate-700 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 truncate">
            {agentConfig.configuration.name}
          </h1>
          <span className="text-slate-500 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
            {agentType}
          </span>
          <span className="text-slate-300 flex-shrink-0">|</span>
          <span className="text-xs text-slate-500 flex-shrink-0">
            v{activeVersion?.version || agentConfig.version}
          </span>
          <button
            onClick={handleToggleActive}
            disabled={deactivateMutation.isPending || reactivateMutation.isPending}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${
              agentConfig.is_active ? 'bg-emerald-500' : 'bg-slate-300'
            } ${(deactivateMutation.isPending || reactivateMutation.isPending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title={agentConfig.is_active ? t.deactivateAgent : t.activateAgent}
            role="switch"
            aria-checked={agentConfig.is_active}
          >
            <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform duration-200 ${
              agentConfig.is_active ? 'translate-x-[18px]' : 'translate-x-[3px]'
            }`} />
          </button>
          <span className={`text-xs flex-shrink-0 ${agentConfig.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
            {agentConfig.is_active ? t.active : t.inactive}
          </span>
        </div>

        {/* Description Row (truncated) */}
        {agentConfig.configuration.description && (
          <p className="text-sm text-slate-500 line-clamp-2 ml-8" title={agentConfig.configuration.description}>
            {agentConfig.configuration.description}
          </p>
        )}

        {/* Action Buttons Row */}
        <div className="flex items-center justify-between ml-8">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{t.updated}: {new Date(activeVersion?.updated_at || agentConfig.updated_at).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCopyDialog(true)}
              title={t.copy}
            >
              <Copy className="w-4 h-4 mr-1" />
              {t.copy}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPromptPreview(true)}
              title={t.promptPreview?.title}
            >
              <Eye className="w-4 h-4 mr-1" />
              {t.promptPreview?.buttonLabel}
            </Button>
            <span className="text-slate-200">|</span>
            {isDirty && (
              <span className="text-xs text-amber-600 px-2">{t.unsavedChanges}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (agentConfig?.configuration) {
                  setFormData(agentConfig.configuration);
                  setIsDirty(false);
                  clearAllErrors();
                }
              }}
              disabled={!isDirty}
            >
              {tc.reset}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending || hasValidationErrors || !imageConfigValid}
            >
              {updateMutation.isPending ? t.saving : ta.saveChanges}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
        <TabsList className="flex-wrap">
          {visibleTabs.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {getTabLabel(tab.id)}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Basic Tab */}
        <TabsContent value="basic" className="mt-6">
          <BasicTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            errors={validationErrors}
            highlightSection={highlightSection}
            navigation={navigation}
            onImageConfigValidityChange={setImageConfigValid}
          />
        </TabsContent>

        {/* Hooks Tab */}
        <TabsContent value="hooks" className="mt-6">
          <Card className="p-6">
            <HooksConfigPanel
              config={formData}
              onConfigUpdate={handleHooksConfigUpdate}
              onNavigateToTab={handleNavigateToTab}
              appContext={appContext}
            />
          </Card>
        </TabsContent>

        {/* Memory Tab (conditional) */}
        <TabsContent value="memory" className="mt-6">
          <MemoryTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            highlightSection={highlightSection}
            errors={validationErrors}
          />
        </TabsContent>

        {/* Knowledge Tab (conditional) */}
        <TabsContent value="knowledge" className="mt-6">
          <KnowledgeTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Astrology Tab (conditional) */}
        <TabsContent value="astrology" className="mt-6">
          <AstrologyTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Personality Tab */}
        <TabsContent value="personality" className="mt-6">
          <PersonalityTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Emotion Tab (conditional) */}
        <TabsContent value="emotion" className="mt-6">
          <EmotionTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
          />
        </TabsContent>

        {/* Classifier Tab (conditional) */}
        <TabsContent value="classifier" className="mt-6">
          <ClassifierTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            agentType={agentType || ''}
            highlightSection={highlightSection}
            errors={validationErrors}
          />
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="mt-6">
          <div className="space-y-6">
            {/* Tool Combo Section */}
            <ToolComboSection
              config={formData}
              onChange={handleFieldChange}
            />

            {/* Auto Tool Match */}
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <Wand2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900">
                  {language === 'zh' ? '自动工具匹配' : 'Auto Tool Match'}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {language === 'zh'
                    ? '基于当前提示词内容，自动识别并推荐需要配置的工具'
                    : 'Automatically identify and suggest tools based on current prompt content'}
                </p>
              </div>
              <button
                onClick={handleAutoToolMatch}
                disabled={!formData.instructions?.trim() && !formData.additional_context?.trim()}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  formData.instructions?.trim() || formData.additional_context?.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={
                  !formData.instructions?.trim() && !formData.additional_context?.trim()
                    ? (language === 'zh' ? '请先在"提示词"标签页中配置系统提示词' : 'Please configure system prompt in the "Prompts" tab first')
                    : undefined
                }
              >
                <Wand2 className="w-4 h-4" />
                {language === 'zh' ? '开始匹配' : 'Match'}
              </button>
            </div>

            {/* Tool Execution & Compression - side by side on widescreen */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {groupedFields['tools_execution'] && (
                <FieldSection
                  sectionKey="tools_execution"
                  sectionConfig={FIELD_SECTIONS['tools_execution']}
                  fields={groupedFields['tools_execution']}
                  values={appContext}
                  onChange={handleFieldChange}
                  appContext={appContext}
                  errors={validationErrors}
                />
              )}
              {groupedFields['tools_compression'] && (
                <div className="flex flex-col">
                  <FieldSection
                    sectionKey="tools_compression"
                    sectionConfig={FIELD_SECTIONS['tools_compression']}
                    fields={groupedFields['tools_compression']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    errors={validationErrors}
                  />
                  {getConfigValue(appContext, 'tools.compression.enabled') === true && (
                    <div className="px-4 pb-4 -mt-2 space-y-2">
                      <p className="text-xs text-gray-500 font-medium">快速预设</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          className="flex flex-col items-start px-3 py-1.5 text-xs rounded border border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          onClick={() => {
                            handleFieldChange('tools.compression.results_limit', 50);
                            handleFieldChange('tools.compression.token_limit', 32000);
                          }}
                        >
                          <span className="font-medium">保守</span>
                          <span className="text-gray-400">占星/数值类 · token≥32K 触发</span>
                        </button>
                        <button
                          type="button"
                          className="flex flex-col items-start px-3 py-1.5 text-xs rounded border border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          onClick={() => {
                            handleFieldChange('tools.compression.results_limit', 5);
                            handleFieldChange('tools.compression.token_limit', 30000);
                          }}
                        >
                          <span className="font-medium">均衡</span>
                          <span className="text-gray-400">通用对话 · 多工具混合</span>
                        </button>
                        <button
                          type="button"
                          className="flex flex-col items-start px-3 py-1.5 text-xs rounded border border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          onClick={() => {
                            handleFieldChange('tools.compression.results_limit', 3);
                            handleFieldChange('tools.compression.token_limit', null);
                          }}
                        >
                          <span className="font-medium">激进</span>
                          <span className="text-gray-400">搜索密集 · 长文研究型</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tool Configuration Sections */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {groupedFields['custom_tools'] && (
                <div className={FIELD_SECTIONS['custom_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="custom_tools"
                    sectionConfig={FIELD_SECTIONS['custom_tools']}
                    fields={groupedFields['custom_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    errors={validationErrors}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}

              {groupedFields['builtin_tools'] && (
                <div className={FIELD_SECTIONS['builtin_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="builtin_tools"
                    sectionConfig={FIELD_SECTIONS['builtin_tools']}
                    fields={groupedFields['builtin_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    errors={validationErrors}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}

              {groupedFields['mcp_tools'] && (
                <div className={FIELD_SECTIONS['mcp_tools'].layout === 1 ? 'xl:col-span-2' : ''}>
                  <FieldSection
                    sectionKey="mcp_tools"
                    sectionConfig={FIELD_SECTIONS['mcp_tools']}
                    fields={groupedFields['mcp_tools']}
                    values={appContext}
                    onChange={handleFieldChange}
                    appContext={appContext}
                    errors={validationErrors}
                    onBrowseTools={handleBrowseTools}
                  />
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Prompts Tab */}
        <TabsContent value="prompts" className="mt-6">
          <div className="space-y-6">
            {/* Quick Response Section — only visible when quick_chat model is enabled */}
            {groupedFields['quick_response'] && getConfigValue(formData, 'hooks.quick_chat.enabled') && (
              <FieldSection
                sectionKey="quick_response"
                sectionConfig={FIELD_SECTIONS['quick_response']}
                fields={groupedFields['quick_response']}
                values={appContext}
                onChange={handleFieldChange}
                appContext={appContext}
                errors={validationErrors}
                highlight={highlightSection === 'quick_response'}
              />
            )}

            {/* System Prompts Section */}
            {groupedFields['prompts'] && (
              <FieldSection
                sectionKey="prompts"
                sectionConfig={FIELD_SECTIONS['prompts']}
                fields={groupedFields['prompts']}
                values={appContext}
                onChange={handleFieldChange}
                appContext={appContext}
                errors={validationErrors}
              />
            )}

            {/* Classifier Advanced Settings */}
            {groupedFields['response_classifier'] && (
              <FieldSection
                sectionKey="response_classifier"
                sectionConfig={FIELD_SECTIONS['response_classifier']}
                fields={groupedFields['response_classifier']}
                values={appContext}
                onChange={handleFieldChange}
                appContext={appContext}
                errors={validationErrors}
              />
            )}

            {/* Template Selector */}
            <TemplateSelector
              value={formData.prompt_template_id ?? null}
              onChange={(templateId) => handleFieldChange('prompt_template_id', templateId)}
            />
          </div>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="mt-6">
          <AdvancedTab
            config={formData}
            onChange={handleFieldChange}
            appContext={appContext}
            highlightSection={highlightSection}
            errors={validationErrors}
          />
        </TabsContent>

        {/* Versions Tab (includes JSON preview) */}
        <TabsContent value="versions" className="mt-6">
          <VersionHistory
            agentType={agentType || ''}
            currentVersion={agentConfig.version}
            currentConfig={formData}
            onConfigReplace={(newConfig) => {
              setFormData(newConfig as AgentConfiguration);
              setIsDirty(true);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Tool Browser Modal */}
      {toolBrowserMode !== 'mcp' && (
        <ToolsBrowser
          isOpen={toolBrowserOpen}
          mode={toolBrowserMode as ToolMode}
          initialConfig={getFieldValue(toolBrowserField)}
          onClose={() => setToolBrowserOpen(false)}
          onApply={handleToolsApply}
        />
      )}
      {toolBrowserMode === 'mcp' && (
        <MCPToolsBrowser
          isOpen={toolBrowserOpen}
          initialConfig={getFieldValue(toolBrowserField)}
          onClose={() => setToolBrowserOpen(false)}
          onApply={handleToolsApply}
        />
      )}

      {/* Auto Tool Match Modal */}
      <AutoToolMatchModal
        isOpen={autoToolMatch.isOpen}
        onClose={autoToolMatch.closeMatch}
        results={autoToolMatch.results}
        selectedTools={autoToolMatch.selectedTools}
        isLoading={autoToolMatch.isLoading}
        onToggleSelection={autoToolMatch.toggleSelection}
        onSelectAll={autoToolMatch.selectAll}
        onDeselectAll={autoToolMatch.deselectAll}
        onConfirm={handleAutoToolMatchConfirm}
      />

      {/* Copy Agent Dialog */}
      <CreateAgentDialog
        isOpen={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        onSuccess={(newAgentType) => navigate(`/agents/${newAgentType}`)}
        copyFrom={{
          agentType: agentType || '',
          config: formData,
        }}
      />

      {/* Prompt Preview Dialog */}
      {showPromptPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPromptPreview(false)}
          />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-slate-900">
                  {t.promptPreview?.title}
                </h2>
              </div>
              <button
                onClick={() => setShowPromptPreview(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <AgentPromptPreview
                templateId={formData.prompt_template_id ?? null}
                agentType={agentType || ''}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {updateMutation.isError && conflictInfo === null && (
        <div className="fixed top-20 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t.saveFailed}: {(updateMutation.error as Error).message}
        </div>
      )}

      {/* Success Toast */}
      {updateMutation.isSuccess && (
        <div className="fixed top-20 right-4 bg-emerald-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {t.saveSuccess}
        </div>
      )}

      {/* Conflict resolution modal */}
      <Modal
        isOpen={conflictInfo !== null}
        onClose={() => { updateMutation.reset(); setConflictInfo(null); }}
        title={t.conflictModalTitle}
        size="md"
      >
        <p
          className="text-sm text-gray-600 mb-6"
          dangerouslySetInnerHTML={{
            __html: t.conflictModalBody
              .replace('{current}', `<strong>v${conflictInfo?.currentVersion}</strong>`)
              .replace('{expected}', `<strong>v${conflictInfo?.expectedVersion}</strong>`),
          }}
        />
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            onClick={() => {
              // reset() first so isError is false before conflictInfo clears,
              // otherwise the error toast flashes for one render frame
              updateMutation.reset();
              setConflictInfo(null);
              queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType!) });
              setIsDirty(false);
            }}
          >
            {t.conflictModalDiscard}
          </button>
          <button
            className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600"
            onClick={() => {
              // reset() first so isError is false before conflictInfo clears
              updateMutation.reset();
              setConflictInfo(null);
              forceOverrideRef.current = true;
              handleSave();
            }}
          >
            {t.conflictModalForce}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AgentConfigPage;
